import { supabase } from "@/integrations/supabase/client";

export interface CitaBloqueo {
  id: number;
  id_configuracion_cita: number | null;
  fecha_inicio: string; // yyyy-MM-dd
  fecha_fin: string; // yyyy-MM-dd
  hora_inicio: number | null; // 0-23, null = todo el día
  hora_fin: number | null; // 0-23
  motivo: string | null;
  activo: boolean;
}

export type NuevoBloqueo = Omit<CitaBloqueo, "id" | "activo">;

/** Bloqueos vigentes (activo=true) para un set de configuraciones, incluyendo los globales. */
export async function fetchBloqueosVigentes(configIds: number[]): Promise<CitaBloqueo[]> {
  if (configIds.length === 0) return [];
  const { data, error } = await (supabase as any)
    .from("citas_horarios_bloqueos")
    .select("id, id_configuracion_cita, fecha_inicio, fecha_fin, hora_inicio, hora_fin, motivo, activo")
    .or(`id_configuracion_cita.in.(${configIds.join(",")}),id_configuracion_cita.is.null`)
    .eq("activo", true);
  if (error) throw error;
  return data || [];
}

/** Bloqueos vigentes de UNA configuración (para la pantalla de configuración: propios + globales). */
export async function fetchBloqueosDeConfig(configId: number): Promise<CitaBloqueo[]> {
  const { data, error } = await (supabase as any)
    .from("citas_horarios_bloqueos")
    .select("*")
    .or(`id_configuracion_cita.eq.${configId},id_configuracion_cita.is.null`)
    .eq("activo", true)
    .order("fecha_inicio");
  if (error) throw error;
  return data || [];
}

export async function crearBloqueo(payload: NuevoBloqueo, creadoPor: string | null): Promise<void> {
  const { error } = await (supabase as any)
    .from("citas_horarios_bloqueos")
    .insert({ ...payload, creado_por: creadoPor, activo: true });
  if (error) throw error;
}

export async function eliminarBloqueo(id: number): Promise<void> {
  const { error } = await (supabase as any).from("citas_horarios_bloqueos").update({ activo: false }).eq("id", id);
  if (error) throw error;
}

function bloqueosDelDia(bloqueos: CitaBloqueo[], configId: number, dateStr: string): CitaBloqueo[] {
  return bloqueos.filter(
    (b) =>
      (b.id_configuracion_cita === configId || b.id_configuracion_cita === null) &&
      b.fecha_inicio <= dateStr &&
      b.fecha_fin >= dateStr
  );
}

/** true si TODO el día está bloqueado para esa configuración (bloqueo sin horas = todo el día). */
export function isDiaBloqueado(bloqueos: CitaBloqueo[], configId: number, dateStr: string): boolean {
  return bloqueosDelDia(bloqueos, configId, dateStr).some((b) => b.hora_inicio === null);
}

/** true si hay un bloqueo GLOBAL (todas las configuraciones) de día completo para esa fecha. */
export function isDiaBloqueadoGlobal(bloqueos: CitaBloqueo[], dateStr: string): boolean {
  return bloqueos.some(
    (b) => b.id_configuracion_cita === null && b.hora_inicio === null && b.fecha_inicio <= dateStr && b.fecha_fin >= dateStr
  );
}

/** true si esa hora puntual (0-23) está bloqueada para esa configuración+fecha. */
export function isHoraBloqueada(bloqueos: CitaBloqueo[], configId: number, dateStr: string, hour: number): boolean {
  const delDia = bloqueosDelDia(bloqueos, configId, dateStr);
  return delDia.some(
    (b) => b.hora_inicio === null || (hour >= (b.hora_inicio as number) && hour <= (b.hora_fin as number))
  );
}
